import { PathItemFragmentDoc } from "@pathwright/web/src/modules/api/generated"
import { getApolloClient } from "@pathwright/web/src/modules/pathwright/PathwrightClient"
import produce from "immer"

const updateCachedPathItemContent = (item, content) => {
  const PATH_ITEM_CONTENT_TYPE_NAME_ID_MAP = {
    course: 19,
    video: 31,
    reading: 32,
    assessment: 46,
    submission: 57,
    audio: 81,
    event: 82,
    completion: 83,
    blocks: 164,
    resource: 170
  }

  const client = getApolloClient(window.school.id)

  const fragment = {
    fragment: PathItemFragmentDoc,
    fragmentName: "PathItem",
    id: `PathItem:${item.id}`
  }

  const fragmentData = client.readFragment(fragment)
  const nextFragmentData = produce(fragmentData, (draft) => {
    draft.content_id = content.id
    draft.content_type = content.type
    draft.item_content_type_id =
      PATH_ITEM_CONTENT_TYPE_NAME_ID_MAP[content.type]
  })
  client.writeFragment({
    ...fragment,
    data: nextFragmentData
  })
}

class ContentAdminStore extends require("lib/static-shim").default(
  require("lib/core/store/backbone-store").default
) {
  static initClass() {
    this.prototype.defaults = {
      method: null, // the content method being viewed
      selection: null, // the content being edited

      is_saving_content: false,
      is_saving_content_silent: false,
      content_error: null
    }

    this.prototype.storeTriggers = {
      "content:updated"() {
        return this.get("selection")
      }
    }

    this.prototype.storeTriggerHandlers = {
      step: {
        "set:step"() {
          return this.action.clearContent()
        }
      }
    }

    this.prototype.actions = {
      async selectBlocksContent() {
        const step = this.getStore("step").get("step").toJSON()
        // save content if ...
        const shouldSaveContent =
          // ... not currently saving ...
          (!this.getState().is_saving_content_silent &&
            // ... and no content ...
            (!step.content || !step.content.id)) ||
          // ... or has content, but is not blocks content
          (step.content && step.content.type !== "blocks")
        if (shouldSaveContent) {
          const Content = this.request.getContentModel("blocks")
          const method = "blocks"
          const selection = new Content()
          this._mutate({ selection, method })
          const content = await this.action.saveSilent({}).promise
          // now, update the Apollo cache to ensure path item has new content type
          updateCachedPathItemContent(step, {
            id: content.id,
            type: "blocks"
          })
        }
      },

      saveSilent(contentData) {
        const isSavingKey = "is_saving_content_silent"

        if (this.get(isSavingKey)) {
          console.warn("saveSilent triggered when already saving... denied!")
          return
        }

        return this.action.saveContent(
          contentData,
          true /* clearOnSave */,
          isSavingKey
        )
      },

      saveContent(contentData, clearOnSave, isSavingKey = "is_saving_content") {
        if (clearOnSave == null) {
          clearOnSave = true
        }
        const content = this.get("selection")
        const method = this.get("method")
        if (!content || !method) {
          throw new Error("No content or method selected")
        }
        const step = this.getStore("step").get("step")
        content.set(contentData)
        content.set("step_id", step.id)
        content.set({ method, selected_option: method })
        this._mutate({ [isSavingKey]: true, content_error: null })
        return new Promise(
          function (resolve, reject) {
            return content
              .save()
              .then((result) => {
                // update Apollo cache
                updateCachedPathItemContent(step, {
                  id: result.id,
                  type: content.get("type")
                })

                this.storeTrigger("content:updated")
                if (clearOnSave) {
                  this.action.clearContent()
                }
                return resolve(result)
              })
              .fail((err) => {
                this._mutate({ content_error: err.responseText })
                return reject(err.responseText)
              })
              .always(() => {
                return this._mutate({ [isSavingKey]: false })
              })
          }.bind(this)
        )
      },

      selectNewContent(type) {
        const Content = this.request.getContentModel(type)
        const method = null
        const selection = new Content()
        return this._mutate({ selection, method })
      },

      selectContent(type, content) {
        const Content = this.request.getContentModel(type)
        const method = (content != null ? content.method : undefined) || null
        const selection = new Content(content)
        return this._mutate({ selection, method })
      },

      clearContent() {
        return this._mutate({ method: null, selection: null })
      },

      selectMethod(method) {
        return this._mutate({ method })
      },

      clearMethod() {
        return this._mutate({ method: null })
      }
    }

    this.prototype.requests = {
      getContentModel(type) {
        const Content = require(`content/${type}/models/${type}`).default
        return Content
      }
    }
  }
}
ContentAdminStore.initClass()

export default window.App.stores.registerStore(
  "contentAdmin",
  ContentAdminStore
)
